import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  AlertTriangle,
  ArrowDownToLine,
  ArrowLeftRight,
  ArrowUpFromLine,
  Building2,
  Landmark,
  List,
  RefreshCw,
  Store,
  Wallet,
} from 'lucide-react';
import { usePermissions } from '../../context/PermissionsContext';
import {
  formatMoney,
  formatDateTime,
} from './treasuryUi';
import { TreasuryErrorPanel, TreasurySkeleton } from './TreasuryFeedback';

export type TreasurySummaryPayload = {
  legalEntity?: { id: string; code: string; legalName: string; baseCurrency?: string };
  positionAt?: string;
  currency?: string;
  kpis?: Record<string, number | null | undefined>;
  centralTreasury?: RegisterRow[];
  branchMasters?: RegisterRow[];
  operatives?: RegisterRow[];
  banks?: BankRow[];
  registers?: RegisterRow[];
};

type RegisterRow = {
  id: string;
  code: string;
  name?: string | null;
  type: string;
  currency?: string;
  balance: number;
  branch?: { id: string; code: string; name: string } | null;
  openSession?: { id: string; openedAt: string } | null;
  lastMovementAt?: string | null;
  linkedBankAccountIds?: string[];
};

type BankRow = {
  id: string;
  bankName: string;
  alias?: string | null;
  accountNumber?: string | null;
  currency?: string;
  balance: number;
  branch?: { id: string; code: string; name: string } | null;
  linkedRegisterCount?: number;
  lastMovementAt?: string | null;
};

type Props = {
  summary: TreasurySummaryPayload | null;
  loading: boolean;
  error: string;
  legalEntityId: string | null;
  onRefresh: () => void;
  lowBalanceThreshold?: number | null;
};

function balanceTone(
  balance: number,
  threshold: number | null | undefined,
): 'normal' | 'low' | 'empty' {
  if (balance <= 0) return 'empty';
  if (threshold != null && balance < threshold) return 'low';
  return 'normal';
}

const TYPE_BADGE: Record<string, { label: string; className: string }> = {
  CENTRAL_TREASURY: { label: 'Central', className: 'bg-slate-900 text-white' },
  BRANCH_MASTER: { label: 'Madre', className: 'bg-indigo-100 text-indigo-900' },
  OPERATIVE: { label: 'Operativa', className: 'bg-sky-100 text-sky-900' },
  BANK: { label: 'Banco', className: 'bg-emerald-100 text-emerald-900' },
};

type AlertItem = {
  id: string;
  message: string;
  to: string;
  cta: string;
};

type FocusFilter = 'all' | 'CENTRAL_TREASURY' | 'BRANCH_MASTER' | 'OPERATIVE' | 'BANK';

export function TreasuryPositionDashboard({
  summary,
  loading,
  error,
  legalEntityId,
  onRefresh,
  lowBalanceThreshold,
}: Props) {
  const navigate = useNavigate();
  const { can } = usePermissions();
  const canWrite = can('cash', 'write');
  const canReadCash = can('cash', 'read');
  const canReadBanks = can('accounting', 'read');

  const [focus, setFocus] = useState<FocusFilter>('all');

  const currency = summary?.currency || summary?.legalEntity?.baseCurrency || 'ARS';
  const kpis = summary?.kpis ?? {};
  const threshold =
    lowBalanceThreshold ??
    (typeof kpis.lowBalanceThreshold === 'number' ? kpis.lowBalanceThreshold : null);

  const alerts = useMemo((): AlertItem[] => {
    const items: AlertItem[] = [];
    const pending = Number(kpis.pendingTransfers ?? 0);
    if (pending > 0) {
      items.push({
        id: 'pending-transfers',
        message:
          pending === 1
            ? '1 movimiento de fondos espera aprobación'
            : `${pending} movimientos de fondos esperan aprobación`,
        to: '/treasury/approvals',
        cta: 'Revisar',
      });
    }
    const pendingBank = Number(kpis.pendingCashBankTransfers ?? 0);
    if (pendingBank > 0) {
      items.push({
        id: 'pending-cash-bank',
        message:
          pendingBank === 1
            ? '1 depósito/extracción espera aprobación'
            : `${pendingBank} depósitos/extracciones esperan aprobación`,
        to: '/treasury/approvals',
        cta: 'Revisar',
      });
    }
    const diffs = Number(kpis.cashDifferences ?? 0);
    if (diffs > 0) {
      items.push({
        id: 'diffs',
        message:
          diffs === 1
            ? '1 arqueo cerrado con diferencia'
            : `${diffs} arqueos cerrados con diferencia`,
        to: '/treasury/supervision?tab=arqueos',
        cta: 'Ver arqueos',
      });
    }
    const stale = Number(kpis.staleOpenSessions ?? 0);
    if (stale > 0) {
      items.push({
        id: 'stale',
        message:
          stale === 1
            ? '1 sesión abierta desde el día anterior'
            : `${stale} sesiones abiertas desde el día anterior`,
        to: '/treasury/supervision?tab=open',
        cta: 'Ver sesiones',
      });
    }
    const noLink = Number(kpis.centralWithoutBankLink ?? 0);
    if (noLink > 0) {
      items.push({
        id: 'unlink',
        message:
          noLink === 1
            ? 'Tesorería Central sin cuenta bancaria vinculada'
            : `${noLink} centrales sin cuenta bancaria vinculada`,
        to: '/treasury/banks?link=1',
        cta: 'Vincular',
      });
    }
    const emptyOps = (summary?.operatives ?? []).filter((r) => r.balance <= 0).length;
    if (emptyOps > 0 && items.length < 4) {
      items.push({
        id: 'empty-ops',
        message:
          emptyOps === 1
            ? '1 caja operativa sin fondos'
            : `${emptyOps} cajas operativas sin fondos`,
        to: '/treasury/cash-registers',
        cta: 'Ver cajas',
      });
    }
    return items.slice(0, 4);
  }, [kpis, summary?.operatives]);

  const rows = useMemo(() => {
    const regs = (summary?.registers ?? []).map((r) => ({
      kind: 'register' as const,
      id: r.id,
      type: r.type,
      name: r.name || r.code,
      branch: r.branch?.name ?? '—',
      balance: r.balance,
      status:
        r.type === 'OPERATIVE'
          ? r.openSession
            ? 'Sesión abierta'
            : 'Sin sesión'
          : 'Sin sesión (estructural)',
      lastMovementAt: r.lastMovementAt,
      tone: balanceTone(r.balance, threshold),
    }));
    const banks = (summary?.banks ?? []).map((b) => ({
      kind: 'bank' as const,
      id: b.id,
      type: 'BANK',
      name: b.alias || b.bankName || b.accountNumber || b.id,
      branch: b.branch?.name ?? '—',
      balance: b.balance,
      status: (b.linkedRegisterCount ?? 0) > 0 ? 'Vinculada' : 'Sin vínculo tesorería',
      lastMovementAt: b.lastMovementAt,
      tone: balanceTone(b.balance, threshold),
    }));
    const all = [...regs, ...banks];
    if (focus === 'all') return all;
    if (focus === 'BANK') return banks;
    return regs.filter((r) => r.type === focus);
  }, [summary, focus, threshold]);

  if (!canReadCash && !canWrite) {
    return (
      <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
        No tenés permiso para ver la posición de tesorería.
      </div>
    );
  }

  if (!legalEntityId) {
    return (
      <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
        Seleccioná una empresa en la barra de contexto para ver la posición.
      </div>
    );
  }

  if (loading && !summary) {
    return <TreasurySkeleton kind="dashboard" />;
  }

  if (error && !summary) {
    return (
      <TreasuryErrorPanel
        title="No pudimos cargar la posición de tesorería"
        message={error}
        onRetry={onRefresh}
      />
    );
  }

  const entityLabel = summary?.legalEntity
    ? `${summary.legalEntity.code} · ${summary.legalEntity.legalName}`
    : '—';
  const positionDate = summary?.positionAt
    ? new Date(summary.positionAt).toLocaleString('es-AR', {
        weekday: 'short',
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    : '—';

  const hasNoRegisters = (summary?.registers?.length ?? 0) === 0;
  const hasNoBanks = (summary?.banks?.length ?? 0) === 0;

  const cardClass =
    'text-left rounded-lg border bg-white p-4 hover:border-slate-400 hover:shadow-sm transition focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-400';

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h2 className="text-xl font-semibold text-slate-900">Posición de tesorería</h2>
          <dl className="mt-2 flex flex-wrap gap-x-6 gap-y-1 text-sm text-slate-600">
            <div>
              <dt className="inline text-slate-500">Empresa · </dt>
              <dd className="inline font-medium text-slate-800">{entityLabel}</dd>
            </div>
            <div>
              <dt className="inline text-slate-500">Fecha de posición · </dt>
              <dd className="inline font-medium text-slate-800">{positionDate}</dd>
            </div>
            <div>
              <dt className="inline text-slate-500">Moneda · </dt>
              <dd className="inline font-medium text-slate-800">{currency}</dd>
            </div>
          </dl>
        </div>
        <button
          type="button"
          onClick={onRefresh}
          disabled={loading}
          className="inline-flex items-center gap-2 px-3 py-2 text-sm border rounded-md hover:bg-slate-50 disabled:opacity-50"
        >
          <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          Actualizar
        </button>
      </div>

      {error && (
        <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2">{error}</p>
      )}

      <section className="rounded-xl border border-slate-200 bg-gradient-to-br from-slate-900 to-slate-700 text-white p-6 sm:p-8">
        <p className="text-sm font-medium text-slate-300 uppercase tracking-wide">Disponible total</p>
        <p className="mt-2 text-3xl sm:text-4xl font-semibold tabular-nums tracking-tight">
          {formatMoney(Number(kpis.totalAvailable ?? 0), currency)}
        </p>
        <p className="mt-2 text-xs text-slate-300 max-w-xl">
          Efectivo en Central, Madres y Operativas (ledger). Los saldos bancarios se muestran aparte.
        </p>
      </section>

      <section className="space-y-3">
        <div className="flex flex-wrap items-end justify-between gap-2">
          <div>
            <h3 className="text-sm font-semibold text-slate-900">Dónde está</h3>
            <p className="text-xs text-slate-500">Efectivo estructural y cuentas bancarias</p>
          </div>
          <p className="hidden md:block text-xs text-slate-500 font-mono">
            Empresa → Central → Madres → Operativas
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <button type="button" className={cardClass} onClick={() => setFocus('CENTRAL_TREASURY')}>
            <div className="flex items-center gap-2 text-xs font-medium text-slate-500">
              <Landmark className="h-4 w-4" /> Tesorería Central
            </div>
            <p className="mt-2 text-xl font-semibold tabular-nums text-slate-900">
              {formatMoney(Number(kpis.centralTreasury ?? 0), currency)}
            </p>
            <p className="mt-1 text-xs text-slate-500">
              {(summary?.centralTreasury?.length ?? 0) || 0} registro
              {(summary?.centralTreasury?.length ?? 0) === 1 ? '' : 's'}
            </p>
          </button>

          <button type="button" className={cardClass} onClick={() => setFocus('BRANCH_MASTER')}>
            <div className="flex items-center gap-2 text-xs font-medium text-slate-500">
              <Store className="h-4 w-4" /> Cajas Madre
            </div>
            <p className="mt-2 text-xl font-semibold tabular-nums text-slate-900">
              {formatMoney(Number(kpis.branchMasters ?? 0), currency)}
            </p>
            <p className="mt-1 text-xs text-slate-500">
              {Number(kpis.masterBranches ?? 0)} sucursal
              {Number(kpis.masterBranches ?? 0) === 1 ? '' : 'es'}
            </p>
          </button>

          <button type="button" className={cardClass} onClick={() => setFocus('OPERATIVE')}>
            <div className="flex items-center gap-2 text-xs font-medium text-slate-500">
              <Wallet className="h-4 w-4" /> Cajas Operativas
            </div>
            <p className="mt-2 text-xl font-semibold tabular-nums text-slate-900">
              {formatMoney(Number(kpis.operatives ?? 0), currency)}
            </p>
            <p className="mt-1 text-xs text-slate-500">
              {Number(kpis.openOperatives ?? 0)} abiertas / {Number(kpis.totalOperatives ?? 0)} totales
            </p>
          </button>

          <button type="button" className={cardClass} onClick={() => setFocus('BANK')}>
            <div className="flex items-center gap-2 text-xs font-medium text-slate-500">
              <Building2 className="h-4 w-4" /> Bancos
            </div>
            <p className="mt-2 text-xl font-semibold tabular-nums text-slate-900">
              {formatMoney(Number(kpis.banksTotal ?? 0), currency)}
            </p>
            <p className="mt-1 text-xs text-slate-500">
              {Number(kpis.bankAccountsCount ?? 0)} cuenta
              {Number(kpis.bankAccountsCount ?? 0) === 1 ? '' : 's'}
            </p>
          </button>
        </div>

        {focus !== 'all' && (
          <button
            type="button"
            className="text-xs text-blue-700 hover:underline"
            onClick={() => setFocus('all')}
          >
            Quitar filtro ({TYPE_BADGE[focus]?.label ?? focus})
          </button>
        )}
      </section>

      {alerts.length > 0 && (
        <section className="space-y-2">
          <h3 className="text-sm font-semibold text-slate-900">Requieren atención</h3>
          <ul className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {alerts.map((a) => (
              <li
                key={a.id}
                className="flex items-center justify-between gap-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5"
              >
                <div className="flex items-start gap-2 min-w-0">
                  <AlertTriangle className="h-4 w-4 text-amber-700 shrink-0 mt-0.5" />
                  <span className="text-sm text-amber-950">{a.message}</span>
                </div>
                <Link
                  to={a.to}
                  className="shrink-0 text-sm font-medium text-amber-900 underline-offset-2 hover:underline"
                >
                  {a.cta}
                </Link>
              </li>
            ))}
          </ul>
        </section>
      )}

      <section className="flex flex-wrap gap-2">
        {canWrite && (
          <>
            <Link
              to="/treasury/move"
              className="inline-flex items-center gap-2 px-3 py-2 rounded-md bg-slate-900 text-white text-sm hover:bg-slate-800"
            >
              <ArrowLeftRight className="h-4 w-4" /> Mover fondos
            </Link>
            <Link
              to="/treasury/move?type=deposit"
              className="inline-flex items-center gap-2 px-3 py-2 rounded-md border text-sm hover:bg-slate-50"
            >
              <ArrowDownToLine className="h-4 w-4" /> Depositar en banco
            </Link>
            <Link
              to="/treasury/move?type=withdrawal"
              className="inline-flex items-center gap-2 px-3 py-2 rounded-md border text-sm hover:bg-slate-50"
            >
              <ArrowUpFromLine className="h-4 w-4" /> Retirar del banco
            </Link>
          </>
        )}
        <Link
          to="/treasury/transfers"
          className="inline-flex items-center gap-2 px-3 py-2 rounded-md border text-sm hover:bg-slate-50"
        >
          <List className="h-4 w-4" /> Ver movimientos
        </Link>
      </section>

      {hasNoRegisters && (
        <div className="rounded-lg border border-dashed px-4 py-6 text-center text-sm text-slate-600">
          No hay cajas activas para esta empresa.{' '}
          <Link to="/branches" className="text-blue-700 hover:underline">
            Configurar en Sucursales
          </Link>
        </div>
      )}
      {!hasNoRegisters && hasNoBanks && (
        <div className="rounded-lg border border-dashed px-4 py-3 text-sm text-slate-600">
          No hay cuentas bancarias cargadas.{' '}
          {canReadBanks ? (
            <Link to="/bank-reconciliation" className="text-blue-700 hover:underline">
              Ir a Conciliación
            </Link>
          ) : (
            <span>Pedí alta de cuentas al área contable.</span>
          )}
        </div>
      )}

      <section className="space-y-2">
        <h3 className="text-sm font-semibold text-slate-900">Fondos por ubicación</h3>
        <div className="overflow-x-auto border rounded-lg bg-white">
          <table className="min-w-full text-sm">
            <thead className="bg-slate-50 text-left text-slate-600">
              <tr>
                <th className="px-3 py-2 font-medium">Tipo</th>
                <th className="px-3 py-2 font-medium">Nombre</th>
                <th className="px-3 py-2 font-medium">Sucursal</th>
                <th className="px-3 py-2 font-medium text-right">Disponible</th>
                <th className="px-3 py-2 font-medium">Estado</th>
                <th className="px-3 py-2 font-medium">Último movimiento</th>
                <th className="px-3 py-2 font-medium">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-3 py-8 text-center text-slate-500">
                    Sin ubicaciones para mostrar con el filtro actual.
                  </td>
                </tr>
              ) : (
                rows.map((r) => {
                  const badge = TYPE_BADGE[r.type] ?? {
                    label: r.type,
                    className: 'bg-slate-100 text-slate-700',
                  };
                  const toneClass =
                    r.tone === 'empty'
                      ? 'text-red-700'
                      : r.tone === 'low'
                        ? 'text-amber-700'
                        : 'text-slate-900';
                  return (
                    <tr key={`${r.kind}-${r.id}`} className="border-t">
                      <td className="px-3 py-2">
                        <span
                          className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${badge.className}`}
                        >
                          {badge.label}
                        </span>
                      </td>
                      <td className="px-3 py-2 font-medium text-slate-800">{r.name}</td>
                      <td className="px-3 py-2 text-slate-600">{r.branch}</td>
                      <td className={`px-3 py-2 text-right tabular-nums font-medium ${toneClass}`}>
                        {formatMoney(r.balance, currency)}
                        {r.tone === 'empty' && (
                          <span className="block text-[10px] font-normal uppercase tracking-wide">
                            Sin fondos
                          </span>
                        )}
                        {r.tone === 'low' && (
                          <span className="block text-[10px] font-normal uppercase tracking-wide">
                            Saldo bajo
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-slate-600">{r.status}</td>
                      <td className="px-3 py-2 text-slate-500 whitespace-nowrap">
                        {formatDateTime(r.lastMovementAt)}
                      </td>
                      <td className="px-3 py-2">
                        {r.kind === 'bank' ? (
                          <Link
                            to={`/treasury/banks?account=${encodeURIComponent(r.id)}`}
                            className="text-blue-700 hover:underline"
                          >
                            Ver en Bancos
                          </Link>
                        ) : (
                          <button
                            type="button"
                            className="text-blue-700 hover:underline"
                            onClick={() =>
                              navigate(`/treasury/cash-registers?focus=${encodeURIComponent(r.id)}`)
                            }
                          >
                            Ver detalle
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}
